import { useScenariosStore } from '../../../core/assistant/stores/scenariosStore';
import { countDeltaChanges } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import { useSuccessionDraftSummary } from './useSuccessionDraftSummary';

/**
 * Everything drafted against a live chart but not yet applied, combined for
 * status surfaces: the client canvas scenario plus the protected server draft.
 *
 * The mode chip's honesty depends on this. A live chart introduces itself as a
 * "read-only view of current data", but the moment a draft exists the canvas is
 * showing current data *plus* pending changes — two UI elements describing two
 * different realities unless the chip is told about the draft.
 */
export function useLiveChartDraftSummary(input: {
  folderId: string | null | undefined;
  diagramId: string | null | undefined;
  enabled?: boolean;
}) {
  const enabled = (input.enabled ?? true) && Boolean(input.folderId && input.diagramId);
  const scenarioChangeCount = useScenariosStore((state) => {
    if (!state.activeScenarioId) return 0;
    const scenario = state.getActiveScenario();
    return scenario && scenario.submissionState !== 'submitted'
      ? countDeltaChanges(scenario.delta) + scenario.changes.length
      : 0;
  });
  const hasEditableActiveScenario = useScenariosStore((state) => {
    const scenario = state.getActiveScenario();
    return scenario != null && scenario.submissionState !== 'submitted';
  });
  const { hasSuccessionDraft, successionChangeCount } = useSuccessionDraftSummary({
    folderId: input.folderId,
    diagramId: input.diagramId,
    enabled,
  });

  const draftChangeCount = (enabled ? scenarioChangeCount : 0)
    + (enabled && hasSuccessionDraft ? successionChangeCount : 0);

  return {
    /** A draft exists on this chart, even if it holds no changes yet. */
    hasDraft: enabled && (hasEditableActiveScenario || hasSuccessionDraft),
    hasClientDraft: enabled && hasEditableActiveScenario,
    clientChangeCount: enabled ? scenarioChangeCount : 0,
    draftChangeCount,
  };
}
